import { z } from 'zod';
import {
  envelopeSchema,
  MapperContext,
  Profile,
  Skill,
  Education,
  Award,
  Certification,
  Project,
  Experience,
  SiteContent,
  strapiProfileSchema,
  strapiSkillSchema,
  strapiEducationSchema,
  strapiAwardSchema,
  strapiCertificationSchema,
  strapiProjectSchema,
  strapiExperienceSchema,
  strapiSiteContentSchema,
  mapProfile,
  mapSkill,
  mapEducation,
  mapAward,
  mapCertification,
  mapProject,
  mapExperience,
  mapSiteContent,
} from '@personal-portfolio/contract';
import { HttpError, NetworkError, ValidationError } from './errors.js';

// Generous on purpose. The CMS is one small Strapi container on SQLite behind
// nginx, and a cold container's first query can take several seconds; a tighter
// timeout turns an ordinary cold start into a visible error page. Anything
// slower than this is an outage, not a slow response.
const REQUEST_TIMEOUT_MS = 10_000;

export interface PortfolioClientConfig {
  /** Strapi origin, e.g. https://cms.example.com (no trailing /api). */
  baseUrl: string;
  /** Optional bearer token. Public reads need none. */
  token?: string;
  /** Injectable fetch for tests/polyfills. Defaults to globalThis.fetch. */
  fetch?: typeof globalThis.fetch;
}

export interface RequestOptions {
  /** Caller-side cancellation; always combined with the 10s client timeout. */
  signal?: AbortSignal;
}

export interface PortfolioClient {
  /** Resolves null when the profile single type has no published entry (404). */
  getProfile(opts?: RequestOptions): Promise<Profile | null>;
  /**
   * Editorial copy, the site's only source of it. Resolves null when the
   * single type has no entry (404); the app treats that as "nothing to
   * render yet" and shows its retry screen, since there is no fallback.
   */
  getSiteContent(opts?: RequestOptions): Promise<SiteContent | null>;
  getSkills(opts?: RequestOptions): Promise<Skill[]>;
  getEducation(opts?: RequestOptions): Promise<Education[]>;
  getAwards(opts?: RequestOptions): Promise<Award[]>;
  getCertifications(opts?: RequestOptions): Promise<Certification[]>;
  getProjects(opts?: RequestOptions): Promise<Project[]>;
  getExperience(opts?: RequestOptions): Promise<Experience[]>;
  /** Origin used to absolutize media URLs; useful for consumers building URLs. */
  readonly baseUrl: string;
}

export const createPortfolioClient = (
  config: PortfolioClientConfig,
): PortfolioClient => {
  const baseUrl = config.baseUrl.replace(/\/+$/, '');
  const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
  const ctx: MapperContext = { mediaBaseUrl: baseUrl };

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(config.token ? { Authorization: `Bearer ${config.token}` } : {}),
  };

  const request = async <T extends z.ZodType>(
    path: string,
    dataSchema: T,
    opts?: RequestOptions & {
      /** Collections get the pagination cap; single types don't. */
      collection?: boolean;
    },
  ): Promise<z.infer<T>> => {
    // Query params are centralized here: everything is populated one level
    // deep, and every collection is capped at 100 items per page.
    //
    // There is no pagination follow-up — one page is the whole collection. 100
    // must therefore stay <= `rest.maxLimit` in apps/strapi-cms/config/api.ts:
    // Strapi silently clamps a pageSize above maxLimit instead of erroring, so
    // lowering it there would quietly truncate every list in the frontend.
    const params = new URLSearchParams({ populate: '*' });
    if (opts?.collection !== false) {
      params.set('pagination[pageSize]', '100');
    }
    const url = `${baseUrl}/api${path}?${params.toString()}`;

    const timeoutSignal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
    const signal = opts?.signal
      ? AbortSignal.any([opts.signal, timeoutSignal])
      : timeoutSignal;

    let response: Response;
    try {
      response = await fetchImpl(url, { headers, signal });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'TimeoutError') {
        throw new NetworkError(
          `Request to ${url} timed out after ${REQUEST_TIMEOUT_MS}ms`,
          cause,
        );
      }
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new NetworkError(`Request to ${url} was aborted`, cause);
      }
      throw new NetworkError(`Request to ${url} failed`, cause);
    }

    if (!response.ok) {
      // Best-effort: an error body is often HTML (an nginx 502/504 page, or a
      // Strapi error page) rather than JSON, so failing to parse it is normal
      // and must not mask the status code the caller actually needs.
      const body = await response.json().catch(() => undefined);
      throw new HttpError(response.status, url, body);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw new ValidationError(
        url,
        null,
        `Expected JSON but got a non-JSON 200 response from ${url}`,
      );
    }

    // Two-step parse: envelope first, then payload. Keeps the generic simple —
    // zod cannot project `.data` through a generic envelope factory.
    const envelope = envelopeSchema.safeParse(json);
    if (!envelope.success) {
      throw new ValidationError(url, envelope.error);
    }
    const parsed = dataSchema.safeParse(envelope.data.data);
    if (!parsed.success) {
      throw new ValidationError(url, parsed.error);
    }
    return parsed.data as z.infer<T>;
  };

  return {
    baseUrl,

    getProfile: async (opts) => {
      try {
        return mapProfile(
          await request('/profile', strapiProfileSchema, {
            ...opts,
            collection: false,
          }),
          ctx,
        );
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },

    getSiteContent: async (opts) => {
      try {
        return mapSiteContent(
          await request('/site-content', strapiSiteContentSchema, {
            ...opts,
            collection: false,
          }),
        );
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },

    getSkills: async (opts) =>
      (await request('/skills', strapiSkillSchema.array(), opts)).map(mapSkill),

    getEducation: async (opts) =>
      (await request('/educations', strapiEducationSchema.array(), opts)).map(
        (item) => mapEducation(item, ctx),
      ),

    getAwards: async (opts) =>
      (await request('/awards', strapiAwardSchema.array(), opts)).map((item) =>
        mapAward(item, ctx),
      ),

    getCertifications: async (opts) =>
      (
        await request(
          '/certifications',
          strapiCertificationSchema.array(),
          opts,
        )
      ).map((item) => mapCertification(item, ctx)),

    getProjects: async (opts) =>
      (await request('/projects', strapiProjectSchema.array(), opts)).map(
        (item) => mapProject(item, ctx),
      ),

    getExperience: async (opts) =>
      (await request('/experiences', strapiExperienceSchema.array(), opts)).map(
        (item) => mapExperience(item, ctx),
      ),
  };
};
